'use client';

import { useEffect } from 'react';
import posthog from 'posthog-js';
import { PostHogProvider, usePostHog } from 'posthog-js/react';
import EngagementTracker from '@/components/EngagementTracker';

function GlobalClickTracker() {
  const posthog = usePostHog();

  useEffect(() => {
    if (!posthog) {
      console.warn('[PostHog] GlobalClickTracker: PostHog instance not available');
      return;
    }

    console.log('[PostHog] GlobalClickTracker: Setting up click tracking');

    const handleClick = (e: Event) => {
      // Find the clicked element or closest parent with .ph-convert class
      const element = (e.target as Element)?.closest('.ph-convert');

      if (element) {
        // Get event name from data attribute or use default
        const eventName = element.getAttribute('data-event') || 'conversion_click';

        // Get additional data from data attributes
        const eventData: Record<string, string> = {
          element_type: element.tagName.toLowerCase(),
          element_text: element.textContent?.trim() || '',
          element_href: (element as HTMLAnchorElement).href || '',
          element_id: element.id || '',
          element_classes: element.className || '',
        };

        // Add any custom data attributes that start with data-ph-
        Array.from(element.attributes).forEach((attr) => {
          if (attr.name.startsWith('data-ph-')) {
            const key = attr.name.replace('data-ph-', '');
            eventData[key] = attr.value;
          }
        });

        // Track the event
        posthog.capture(eventName, eventData);

        if (process.env.NODE_ENV === 'development') {
          console.log('PostHog conversion tracked:', eventName, eventData);
        }
      }
    };

    document.addEventListener('click', handleClick);
    return () => document.removeEventListener('click', handleClick);
  }, [posthog]);

  return null; // This component doesn't render anything
}

export function PHProvider({
  children,
}: {
  children: React.ReactNode;
}) {
  // PostHog init now lives in instrumentation-client.ts (runs before hydration).
  // This provider only wires the already-initialized client into React context
  // so usePostHog() works, and mounts the conversion/engagement trackers.

  // Check if PostHog should be enabled (for GlobalClickTracker)
  const shouldEnablePostHog =
    typeof window !== 'undefined' &&
    !document.cookie.split(';').some((cookie) => cookie.trim().startsWith('traffic_type=internal'));

  return (
    <PostHogProvider client={posthog}>
      {shouldEnablePostHog && <GlobalClickTracker />}
      {shouldEnablePostHog && <EngagementTracker />}
      {children}
    </PostHogProvider>
  );
}
